import Backbone from 'backbone';
import $ from 'jquery';
import React from 'react';
import {debounce} from 'ts-debounce';

import * as analytics from 'analytics/Analytics';
import {assemblies, __Assemblies, Assembly, AssemblySlotType} from 'assembly/AssemblyModels';
import {CreateAssemblyModal} from 'assembly/CreateAssemblyModal';
import SwitcherItemTemplate from 'assembly/SwitcherItemTemplate';
import {assertAndContinue} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {ANALYTICS_MODULE} from 'constants/AnalyticsConstants';
import FileState, {FileStateAttributes} from 'filestate/FileState';
import FileStates from 'FileStates';
import {openBulkAssembleDNA} from 'nav/CreateDropdownActions';
import notifications from 'notifications';
import setTestAttrs from 'test/lib/cypress/setTestAttrs';
import legacyRunModal from 'ui-library/legacyRunModal';
import {SingletonCollection} from 'util/backbone-extensions';
import {listToReadable} from 'util/NameUtil';

class SwitcherItem extends Backbone.View<Assembly> {
  template!: typeof SwitcherItemTemplate;

  static initClass(): void {
    this.prototype.tagName = 'li';
    this.prototype.template = SwitcherItemTemplate;
  }

  override initialize(options: Backbone.ViewOptions<Assembly>): void {
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(this.model, 'change:active', this.toggle);
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(this.model, 'change:name', this.render);
  }

  toggle(assembly: Assembly, active: boolean): void {
    this.$el.toggleClass('open', active);
  }

  override render(): this {
    this.$el.html(this.template(this.model.toJSON()));
    this.$el.data({assemblyId: this.model.id});
    this.toggle(this.model, this.model.get('active'));
    return this;
  }
}

SwitcherItem.initClass();

class Switcher extends Backbone.View<Backbone.Model> {
  override collection!: __Assemblies;
  sequences!: SingletonCollection<FileState, FileStateAttributes>;

  static initClass(): void {
    this.prototype.el = '#assembly-switcher';

    this.prototype.events = {
      // @ts-expect-error
      'click li a': 'handleClick',
      'click .statusbar-label': 'open',
    };
  }

  override initialize(options: Backbone.ViewOptions<Backbone.Model>): void {
    this.collection = assemblies;
    this.sequences = FileStates.get();
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(this.collection, 'add', this.add);
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(this.collection, 'add remove reset finishedFetch', this.renderItems);
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(this.collection, 'change:name change:parameters', debounce(this.saveAssembly, 500));
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(this.sequences, 'remove', this.handleClose);
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(this.sequences, 'reset', this.handleCloseAll);
  }

  open(): void {
    analytics.track('assembly', 'assembly_wizard_click', {product: ANALYTICS_MODULE.MOLBIO});
    if (!this.collection.isLoaded() && !this.collection.isFetching()) {
      this.collection.fetch({reset: true});
    }
  }

  saveAssembly(assembly: Assembly): void {
    assembly.save(null, {patch: true, parse: false});
  }

  handleCloseAll(fileStates: Array<FileState>, options: {previousModels: Array<FileState>}): void {
    for (const fileState of Array.from(options.previousModels)) {
      this.handleClose(fileState);
    }
  }

  handleClose(file: FileState): void {
    const removeID = file.id;
    this.collection.each((model) => {
      // Remove uses of sequence in active assembly
      const fragments = model
        .slots()
        .chain()
        .map((slot: AssemblySlotType) =>
          slot.fragments().filter((fragment) => fragment.sequenceID() === removeID)
        )
        .flatten()
        .value();
      const slots = model
        .slots()
        .filter((slot) => slot.fragments().any((fragment) => fragment.sequenceID() === removeID));
      if (fragments.length && model.active()) {
        for (let i = 0; i < fragments.length; i++) {
          // Only trigger on the last fragment, since fragments are in a bad state for rendering
          // They reference a sequence ID that is no longer open
          const fragment = fragments[i];
          fragment.destroy({
            error() {
              // Assert false for now to get a stack trace to help with debugging T571
              assertAndContinue(false, 'fragment.destroy() failed');
            },
            silent: i !== fragments.length - 1,
          });
        }
        const slotNames = slots.map((slot) => slot.name()!);
        notifications.warn(
          <>
            <strong>{file.name()}</strong> was removed from <strong>{listToReadable(slotNames)}</strong> in
            the current assembly.
          </>
        );
      }
    });
  }

  handleClick(e: JQueryEventObject): void {
    const $li = $(e.currentTarget).parent();

    if ($li.hasClass('create')) {
      analytics.track('assembly', 'create_new_click', {product: ANALYTICS_MODULE.MOLBIO});
      legacyRunModal(CreateAssemblyModal, {});
      return;
    }
    if ($li.hasClass('create-bulk')) {
      analytics.track('assembly', 'create_new_bulk_click', {product: ANALYTICS_MODULE.MOLBIO});
      openBulkAssembleDNA();
      return;
    }

    const assembly = this.collection.get($li.data('assemblyId'));

    if ($(e.target).hasClass('badge-close')) {
      assembly.destroy();
      return;
    }

    if (assembly.active()) {
      this.collection.toggleActive(assembly);
    } else {
      this.collection.activateAssembly(assembly);
    }
  }

  add(assembly: Assembly): void {
    this.collection.each((assembly) => assembly.set({active: false}));
    this.collection.activateAssembly(assembly);
  }

  renderItems(): void {
    if (!this.collection.isLoaded()) {
      return;
    }
    const items: Array<JQuery> = this.collection.map((assembly) => {
      const view = new SwitcherItem({model: assembly});
      return view.render().el;
    });
    if (items.length > 0) {
      items.unshift($('<li>').addClass('divider'));
    }
    const createAssemblyLink = $('<a>').text('Assembly wizard');
    setTestAttrs(createAssemblyLink, {component: 'Switcher', element: 'createAssemblyButton'});

    const links = [{html: createAssemblyLink, className: 'create'}];
    if (BenchlingConfig.ENABLE_BULK_ASSEMBLY) {
      links.push({
        html: $('<a>').text(
          BenchlingConfig.MOLBIO_REDESIGNED_CLONING_FLOW ? 'Combinatorial cloning' : 'Bulk assembly'
        ),
        className: 'create-bulk',
      });
    }

    items.unshift(
      ...links.map(({html, className}) =>
        $('<li>')
          .addClass(className)
          // @ts-expect-error we sometimes use JQuery as htmlString
          .html(html)
      )
    );
    // @ts-expect-error we sometimes use JQuery as htmlString
    this.$('ul').html(items);
  }
}

Switcher.initClass();

export default Switcher;
